import { useMemo } from "react";
import { useGlobalState } from "../GlobalContext";
import { excludeSettings } from "../ProductSettings/FilterSettings";
import { getHardcodedExclusions } from "../ProductSettings/hardcodedExclusions";
import useProductSettingsExclusions from "./useProductSettingsExclusions";

// Single filter pipeline for every configurator surface.
//
// Returns:
//   filteredProductSettings — raw productSettings minus inactive settings and
//                             user-specific exclusions, with allowed-app
//                             overrides honored by FilterSettings.excludeSettings
//   productSettingsExclusions — raw user-specific exclusions (some consumers
//                               use these for non-UI logic, e.g. price calcs)
//
// Admins impersonating a user bypass all filtering — they see everything.
export default function useFilteredProductSettings(loggedInUser) {
    const { productSettings } = useGlobalState();
    const [productSettingsExclusions] = useProductSettingsExclusions(loggedInUser);

    const filteredProductSettings = useMemo(() => {
        if (!Array.isArray(productSettings) || productSettings.length === 0) {
            return [];
        }

        if (loggedInUser?.is_admin_impersonating) {
            return productSettings;
        }

        const allExclusions = [...(productSettingsExclusions ?? []), ...getHardcodedExclusions()];

        const allowed = allExclusions.filter((rule) => rule.isAllowed === true);
        const denied = allExclusions.filter((rule) => rule.isAllowed === false);

        return excludeSettings(denied, allowed, productSettings);
    }, [productSettings, productSettingsExclusions, loggedInUser?.is_admin_impersonating]);

    return [filteredProductSettings, productSettingsExclusions];
}
